import { useEffect, useState } from "react";
import client, { fmt } from "@/lib/api";
import { Panel } from "@/components/Layout";
import { Radar, Home, Building2, Zap, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SENTRY_ICON =
  "https://static.prod-images.emergentagent.com/jobs/8041819d-7954-4bde-9fe6-e6847159f659/images/49cf5e10f74d1624fa5a05138e4e6e0cdb831fe4955b55ca7d3c5aa9b2f72cb1.png";

export default function LeadSentry() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    const { data } = await client.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await client.post("/leads/scan");
      toast.success(`Sentry detected ${data.detected} new milestones`);
      load();
    } catch {
      toast.error("Sentry scan failed");
    } finally {
      setScanning(false);
    }
  };

  const remove = async (id) => {
    await client.delete(`/leads/${id}`);
    load();
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.source === filter);
  const counts = {
    home_sale: leads.filter((l) => l.source === "home_sale").length,
    business_filing: leads.filter((l) => l.source === "business_filing").length,
    hot: leads.filter((l) => l.score >= 80).length,
  };

  return (
    <div className="space-y-6" data-testid="sentry-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 hidden md:block">
            <img src={SENTRY_ICON} alt="Sentry" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="label-tag mb-2">/// MODULE 02 — AUTOMATED MILESTONE DETECTION</div>
            <h1 className="heading-display text-4xl sm:text-5xl">Lead Sentry Gold</h1>
            <p className="text-[#888] text-sm mt-2 max-w-xl">
              Continuous sweep across home sales records and business filings. Each milestone is scored and pushed to the protection queue.
            </p>
          </div>
        </div>
        <button onClick={scan} disabled={scanning} className="btn-gold inline-flex items-center gap-2" data-testid="sentry-scan-btn">
          <Radar className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} />
          {scanning ? "Sweeping..." : "Run Sentry Sweep"}
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Home} label="Home Sale Signals" value={counts.home_sale} testId="stat-home" />
        <Stat icon={Building2} label="Business Filings" value={counts.business_filing} testId="stat-biz" />
        <Stat icon={Zap} label="Hot Leads (≥80)" value={counts.hot} testId="stat-hot" highlight />
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="sentry-filters">
        {[
          { k: "all", l: "All Signals" },
          { k: "home_sale", l: "Home Sales" },
          { k: "business_filing", l: "Business Filings" },
          { k: "referral", l: "Referrals" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`sentry-filter-${t.k}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
              filter === t.k ? "bg-gold text-black border-gold" : "border-[#332A0C] text-[#888] hover:text-gold hover:border-gold"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <Panel tag={`// ${filtered.length} LIVE SIGNALS`} title="Sentry Feed" testId="sentry-feed">
        <div className="space-y-2">
          {filtered.map((l, idx) => (
            <div
              key={l.id}
              className="grid grid-cols-12 gap-3 items-center p-3 border border-[#1a1a1a] hover:border-gold transition-colors fade-up"
              style={{ animationDelay: `${idx * 0.03}s` }}
              data-testid={`lead-${l.id}`}
            >
              <div className="col-span-12 md:col-span-3">
                <div className="font-bold">{l.full_name}</div>
                <div className="label-tag text-[#888] mt-1">{l.source.replace("_", " ")}</div>
              </div>
              <div className="col-span-6 md:col-span-3">
                <div className="text-xs uppercase tracking-wider text-gold font-bold">{l.milestone}</div>
                <div className="text-[11px] text-[#888]">{l.address}</div>
              </div>
              <div className="col-span-3 md:col-span-2 text-right md:text-left">
                <div className="mono text-sm">{l.contact || "—"}</div>
              </div>
              <div className="col-span-2 md:col-span-2 text-right">
                <div className="mono font-bold">{fmt.money(l.estimated_value)}</div>
                <div className="label-tag text-[#666]">Est. value</div>
              </div>
              <div className="col-span-1 md:col-span-1">
                <ScoreBadge score={l.score} />
              </div>
              <div className="col-span-12 md:col-span-1 flex md:justify-end">
                <button onClick={() => remove(l.id)} className="text-[#666] hover:text-[#FF3B30]" data-testid={`del-lead-${l.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center text-[#666] py-10">No signals yet. Trigger a Sentry sweep.</div>}
        </div>
      </Panel>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId, highlight }) {
  return (
    <div className={`hud-panel p-5 ${highlight ? "border-gold" : ""}`} data-testid={testId}>
      <div className="hud-corner-tl" /><div className="hud-corner-tr" />
      <div className="hud-corner-bl" /><div className="hud-corner-br" />
      <div className="flex items-center justify-between relative z-10">
        <div>
          <div className="label-tag">{label}</div>
          <div className="metric-value text-4xl mt-1">{value}</div>
        </div>
        <Icon className="w-7 h-7 text-gold" />
      </div>
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 80 ? "text-gold border-gold" : score >= 60 ? "text-white border-[#888]" : "text-[#888] border-[#332A0C]";
  return (
    <div className={`text-center border ${color} px-2 py-1`}>
      <div className="mono text-lg font-bold">{score}</div>
      <div className="label-tag text-[9px]">SCORE</div>
    </div>
  );
}
